import { ProductCardProps } from '../../home/types/types';
import Icon from '../../common/components/Icon';
import React, { useMemo, useState } from 'react';
import ProductsBlock from '../components/ProductsBlock';
import Breadcrumbs from '../../components/breadcrumb/Breadcrumb';
import Tabs from '../../components/tabs/Tabs';
import { TabProps } from '../../components/tab/Tab';
import { getProducts } from '../services/EcommerceService';
import { useTranslations } from '../../localization/useTranslations';
import useViewItemListEvent from '../../hooks/useViewItemListEvent';

export interface BrandDetailPageProps {
  name: string;
  description: string;
  logo: {
    url: string;
    alt: string;
  };
  products: ProductCardProps[];
  categories: {
    name: string;
    description: string;
    id: string;
    slug: string;
  }[];
  brandId: number;
}

const BrandDetailPage: React.FC<BrandDetailPageProps> = ({
  name,
  description,
  logo,
  products,
  categories,
  brandId,
}) => {
  const [expanded, setExpanded] = React.useState(false);
  const [loading, setLoading] = useState(false);
  const [productItems, setProductItems] = useState<ProductCardProps[]>(products);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 8;
  const [hasNext, setHasNext] = useState(products.length >= itemsPerPage);
  const showExpandButton = description.length >= 500;
  const translations = useTranslations();

  useViewItemListEvent('view_item_list', brandId, name, productItems);

  const tabs = useMemo<TabProps[]>(() => {
    const categoryTabs = categories.map((category) => ({
      label: category.name,
      link: `category=${category.slug}`,
    }));

    const allProductsTab = {
      label: translations?.ecommerce?.allProducts || 'All products',
      link: 'category=',
    };

    return [allProductsTab, ...categoryTabs];
  }, [categories, translations?.ecommerce?.allProducts]);

  const handleLoadMore = async () => {
    if (!brandId) {
      console.error('Brand ID is not defined');
      return;
    }

    try {
      setLoading(true);
      const { products, hasNext } = await getProducts({
        brandId,
        page: currentPage + 1,
        items_per_page: itemsPerPage,
      });

      setHasNext(hasNext);
      setProductItems((prevProducts) => [...prevProducts, ...products]);
      setCurrentPage((prevPage) => prevPage + 1);
    } catch (error) {
      console.error('Error loading more products:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="ecom-brand-detail">
      <Breadcrumbs
        firstHasPreIcon
        breadcrumbs={[
          {
            label: translations?.ecommerce?.shopping || 'Shopping',
            path: '/market/',
          },
          {
            label: translations?.ecommerce?.brands || 'Brands',
            path: '/market/znacky/',
          },
          { label: name, path: `/market/znacka/${name}` },
        ]}
      />
      {description ||
        (logo?.url && (
          <div className="ecom-brand-detail__wrapper">
            <div className="ecom-brand-detail__description-wrapper">
              <h5
                className={`ecom-brand-detail__description ${
                  expanded ? 'expanded' : ''
                } ${!showExpandButton ? 'not-truncated' : ''}`}
              >
                {description}
              </h5>
              {showExpandButton && (
                <button
                  className={`ecom-brand-detail__expand-btn link-16 underline ${expanded ? 'expanded' : ''}`}
                  onClick={() => setExpanded(!expanded)}
                >
                  {translations?.ecommerce?.expandButton || 'Expand'}
                  {!expanded ? (
                    <Icon name="ecom-arrow-down" size="small" />
                  ) : (
                    <Icon name="ecom-arrow-up" size="small" />
                  )}
                </button>
              )}
            </div>

            {logo?.url && (
              <div className="ecom-brand-detail__background">
                <img src={logo.url} alt={logo.alt} className="ecom-brand-detail__logo" />
              </div>
            )}
          </div>
        ))}

      {/* TODO: Implement when it's ready */}
      <Tabs
        tabs={tabs}
        hasBottomBorder
        onTabClick={(slugWithPrefix) => {
          const [, slug] = slugWithPrefix.split('=');
          const url = new URL(window.location.href);
          url.searchParams.set('category', slug);
          window.location.href = url.toString();
        }}
        fontSize={14}
      />

      <div className="ecom-brand-detail__products">
        {productItems && (
          <ProductsBlock
            products={productItems}
            button={
              hasNext
                ? {
                    label: translations?.ecommerce?.loadMore || 'Load more',
                    onClick: () => handleLoadMore(),
                    loading: loading,
                  }
                : undefined
            }
          />
        )}
      </div>
    </div>
  );
};

export default BrandDetailPage;
